import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, BookOpenCheck, Calendar, Layers } from 'lucide-react'
import { api } from '../services/api'
import { Course, Material } from '../types'
import LoadingSpinner from '../components/LoadingSpinner'

const DEFAULT_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444']

function daysUntil(iso?: string): number | null {
  if (!iso) return null
  const target = new Date(iso).getTime()
  const now = Date.now()
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24))
}

export default function CoursesPage() {
  const [courses, setCourses] = useState<Course[]>([])
  const [materials, setMaterials] = useState<Material[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [c, m] = await Promise.all([
        api.listCourses(),
        api.getMaterials() as Promise<{ materials: Material[] }>,
      ])
      setCourses(c.courses)
      setMaterials((m as any).materials ?? (m as any) ?? [])
    } catch (e: any) {
      setError(e.message || 'No se pudieron cargar los cursos')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Mis Cursos</h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            Agrupa materiales por materia o examen. Genera quizzes combinados.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors"
          style={{ background: '#4F46E5', color: '#fff' }}
        >
          <Plus className="w-4 h-4" />
          Crear curso
        </button>
      </div>

      {error && (
        <div
          className="p-4 rounded-xl text-sm"
          style={{ background: 'rgba(244,63,94,0.08)', color: '#FB7185', border: '1px solid rgba(244,63,94,0.2)' }}
        >
          {error}
        </div>
      )}

      {courses.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(79,70,229,0.1)' }}
          >
            <BookOpenCheck className="w-7 h-7" style={{ color: '#4F46E5' }} />
          </div>
          <p className="font-medium mb-1" style={{ color: '#E2E8F0' }}>
            Aún no tienes cursos
          </p>
          <p className="text-sm" style={{ color: '#64748B' }}>
            Crea un curso para preparar un examen combinando varios materiales.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => {
            const d = daysUntil(c.examDate)
            return (
              <Link
                key={c.courseId}
                to={`/courses/${c.courseId}`}
                className="glass-card p-5 block transition-transform hover:-translate-y-1"
                style={{ borderLeft: `4px solid ${c.color || '#8b5cf6'}` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg" style={{ color: '#F1F5F9' }}>{c.name}</h3>
                  <Layers className="w-4 h-4" style={{ color: '#64748B' }} />
                </div>
                <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                  {(c.materialIds || []).length} material{(c.materialIds || []).length === 1 ? '' : 'es'}
                </p>
                {d !== null && (
                  <div className="flex items-center gap-2 text-xs" style={{ color: d <= 7 ? '#FB7185' : '#94A3B8' }}>
                    <Calendar className="w-3 h-3" />
                    {d > 0 ? `${d} día${d === 1 ? '' : 's'} al examen` : d === 0 ? 'Hoy es el examen' : 'Examen pasado'}
                  </div>
                )}
              </Link>
            )
          })}
        </div>
      )}

      {showCreate && (
        <CreateCourseModal
          materials={materials}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load() }}
        />
      )}
    </div>
  )
}

function CreateCourseModal({
  materials,
  onClose,
  onCreated,
}: {
  materials: Material[]
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [color, setColor] = useState(DEFAULT_COLORS[0])
  const [examDate, setExamDate] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggleMaterial = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const submit = async () => {
    if (!name.trim()) { setError('Ponle un nombre al curso'); return }
    setSaving(true); setError(null)
    try {
      await api.createCourse({
        name: name.trim(),
        color,
        examDate: examDate || undefined,
        description: description || undefined,
        materialIds: selectedIds,
      })
      onCreated()
    } catch (e: any) {
      setError(e.message || 'No se pudo crear el curso')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div
        className="glass-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4" style={{ color: '#F1F5F9' }}>Nuevo curso</h2>

        {error && (
          <div className="p-3 mb-3 rounded text-sm" style={{ background: 'rgba(244,63,94,0.1)', color: '#FB7185' }}>
            {error}
          </div>
        )}

        <label className="block text-sm mb-1" style={{ color: '#94A3B8' }}>Nombre</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej. Biología — examen marzo"
          maxLength={80}
          className="w-full px-3 py-2 rounded-lg mb-4 text-sm"
          style={{ background: 'rgba(255,255,255,0.05)', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.1)' }}
        />

        <label className="block text-sm mb-1" style={{ color: '#94A3B8' }}>Fecha del examen (opcional)</label>
        <input
          type="date"
          value={examDate}
          onChange={(e) => setExamDate(e.target.value)}
          className="w-full px-3 py-2 rounded-lg mb-4 text-sm"
          style={{ background: 'rgba(255,255,255,0.05)', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.1)' }}
        />

        <label className="block text-sm mb-1" style={{ color: '#94A3B8' }}>Color</label>
        <div className="flex gap-2 mb-4">
          {DEFAULT_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className="w-8 h-8 rounded-full"
              style={{
                background: c,
                outline: color === c ? '2px solid #fff' : 'none',
                outlineOffset: 2,
              }}
            />
          ))}
        </div>

        <label className="block text-sm mb-1" style={{ color: '#94A3B8' }}>
          Materiales ({selectedIds.length} seleccionados)
        </label>
        <div
          className="rounded-lg p-2 mb-4 max-h-48 overflow-y-auto"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {materials.length === 0 ? (
            <p className="text-xs p-2" style={{ color: '#64748B' }}>
              No hay materiales. Sube uno primero.
            </p>
          ) : (
            materials.map((m) => (
              <label key={m.materialId} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-white/5 rounded">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(m.materialId)}
                  onChange={() => toggleMaterial(m.materialId)}
                />
                <span style={{ color: '#E2E8F0' }} className="truncate flex-1">{m.title}</span>
              </label>
            ))
          )}
        </div>

        <label className="block text-sm mb-1" style={{ color: '#94A3B8' }}>Descripción (opcional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={500}
          className="w-full px-3 py-2 rounded-lg mb-4 text-sm"
          style={{ background: 'rgba(255,255,255,0.05)', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.1)' }}
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm"
            style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8' }}
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium"
            style={{ background: '#4F46E5', color: '#fff', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Creando…' : 'Crear curso'}
          </button>
        </div>
      </div>
    </div>
  )
}
